/* The box the surface sits in: two back walls with real thickness, a floor, and the crosshair that
   locates a point against them.

   Geometry only, in the unit cube the projector takes — x and y in [-1, 1], z in [0, 1]. Kept
   apart from the drawing so the arithmetic is testable without a canvas, and so the same corner
   choice drives the walls and the crosshair rather than each working it out again.

   Which two walls is not a fixed choice: `getBackCorner` already tracks the corner furthest from
   the eye per frame, and the walls are the two faces meeting it. Any other pair would stand
   between the eye and the data.

   Every part of the box is a slab rather than a plane. A plane can only ever be a plane with a line around it -
   what reads as dimension is seeing the wall's own top and end faces at an angle, and the diagonal
   where the two walls' top faces meet. That diagonal is a mitre, and it is the whole effect: the
   one line in the drawing that could not exist if the walls were flat. */

import type { UnitBounds } from '../geometry/fitZoom';
import type { BackCorner } from '../geometry/projection';
import { toTickFractions, type AxisInset } from '../data/horizontalAxes';

/** A point in the unit cube the projector accepts. */
export type UnitPoint = [x: number, y: number, z: number];

export type UnitSegment = [from: UnitPoint, to: UnitPoint];

/**
 * How thick the walls are, in unit-cube units, where the plane is 2 across.
 *
 * A fraction of the box rather than a pixel count, so it foreshortens with everything else and
 * holds its proportion at any zoom - the thickness is part of the shape, not an outline drawn on
 * top of it.
 */
export const AXIS_BOX_THICKNESS = 0.015;

/**
 * How far the plinth hangs below the plane, in unit-cube z.
 *
 * Divided by the height scale for the same reason `floorSlab` is: the projector applies that scale
 * to z alone, so this is what makes the plinth as deep on screen as the walls are wide. Exported
 * because `renderAxes3d` has to clear it - the horizontal tick labels hang off the same front edge
 * the slab does - and two copies of the arithmetic would drift.
 */
export const axisBoxBase = (thickness: number, heightScale: number): number =>
  -thickness / heightScale;

/**
 * The axis-aligned envelope of every corner the axis box can occupy.
 *
 * The walls only reach outward on the two sides meeting the back corner and the plinth hangs below
 * the plane, but which two sides depends on the yaw - so this is a superset by the thickness on two
 * sides, which costs a fraction of a percent of zoom and cannot under-reserve as the camera turns.
 */
export const axisBoxBounds = (heightScale: number): UnitBounds => ({
  x: [-1 - AXIS_BOX_THICKNESS, 1 + AXIS_BOX_THICKNESS],
  y: [-1 - AXIS_BOX_THICKNESS, 1 + AXIS_BOX_THICKNESS],
  z: [axisBoxBase(AXIS_BOX_THICKNESS, heightScale), 1],
});

export type AxisBoxFaceKind =
  /** The plane under the data, which `renderAxes3d` rules. The top face of the plinth. */
  | 'floor'
  /** The inner face of a wall: the one the grid is ruled on and the data is read against. */
  | 'wall'
  /**
   * A face of a wall's thickness - its top, its far side, its free end.
   *
   * Kept apart from the plinth's because of when each can be drawn. A wall rim stands at the back
   * and at full height, so the surface can rise in front of it and it has to be painted before the
   * data. The plinth's cannot: see below.
   */
  | 'wallRim'
  /**
   * The bottom of the plinth, which spans the whole footprint.
   *
   * Its own kind because it is the one face of the box that can hide the data. It is visible only
   * when the camera is under the plane, where the floor is culled and this quad covers everything
   * the surface projects into - so it is painted before the mesh, as the wall rims are, and the
   * surface draws over it. The rest of the plinth cannot reach the data and is painted after.
   */
  | 'plinthBase'
  /**
   * A side of the plinth's thickness.
   *
   * Painted after the surface while the camera is above the plane, and before it once the camera
   * goes under - `passOf` decides, and the rule is stated there. Where it sits in the unit cube says
   * nothing about whether it covers the data; the projection does, and below the plane every one of
   * these sweeps a band across the footprint. Drawn late where that is safe because drop lines run
   * down to the
   * floor plane on a layer above this one, and the plinth's front faces stand in front of them, so
   * drawn any earlier the lines show straight through the plinth.
   */
  | 'plinthRim';

export type AxisBoxFace = {
  kind: AxisBoxFaceKind;
  /**
   * Outward normal.
   *
   * Carried rather than derived from the winding, because it decides which faces the eye can see
   * and a sign convention read out of projected screen coordinates is one waiting to be wrong.
   * Every face here is axis-aligned, so the normal is exact by construction.
   */
  normal: UnitPoint;
  points: UnitPoint[];
  /**
   * The edges of this face to draw.
   *
   * Usually all of them. Not always: an edge where two slabs meet is interior to the solid, so it
   * has no business being drawn even though the face it belongs to is visible. Carried per face
   * rather than worked out from depth, because whether an edge is interior is a fact about the
   * shape and does not change with the camera.
   */
  outline: UnitSegment[];
};

/** Every edge of a polygon, closing back to the start. */
export const edgesOf = (points: UnitPoint[]): UnitSegment[] =>
  points.map((point, index) => [point, points[(index + 1) % points.length]]);

/**
 * Every edge except the one lying flat where the slab meets the floor plane.
 *
 * For the faces this is applied to - a wall's free end, and the plinth's front sides - that edge is
 * not a crease. Those two faces are *coplanar*: the wall's end face and the plinth's front face
 * below it stand in the same vertical plane, at the same tone, so a line between them cuts across
 * what the eye reads as one flat surface. The only line that belongs along `z = 0` there is the
 * floor's own front edge, which the floor draws.
 */
const edgesClearOfTheFloor = (points: UnitPoint[]): UnitSegment[] =>
  edgesOf(points).filter(
    ([[, , fromZ], [, , toZ]]) => fromZ !== 0 || toZ !== 0
  );

const sign = (value: number): number => (value < 0 ? -1 : 1);

/**
 * One wall as a slab standing on the floor, mitred at the corner end.
 *
 * `axis` says which plane the wall stands in: `'x'` for the wall at `x = back`, `'y'` for the one at
 * `y = back`. The two are the same shape with the coordinates swapped, so one function builds both
 * - the mitre only lines up if they derive the shared corner vertex identically, and two near-copies
 * of this would be two places for that to drift.
 *
 * The mitre face itself is left out. It is where the two slabs meet, so it is interior to their
 * union from every angle, and drawing it would put a wall inside the solid.
 */
const wallSlab = (
  axis: 'x' | 'y',
  [backX, backY]: BackCorner,
  thickness: number
): AxisBoxFace[] => {
  const [back, other] = axis === 'x' ? [backX, backY] : [backY, backX];
  const outer = back + thickness * sign(back);
  /* The corner vertex, shared with the other wall. Both coordinates step outward: that is what
     makes the two top faces meet along a diagonal instead of overlapping in a square. */
  const cornerOuter = other + thickness * sign(other);
  /* The end the data does not reach round, whose face is the one the eye can see. */
  const free = -other;

  /** Places a point in the wall's own frame: across its thickness, along its length, and up. */
  const at = (across: number, along: number, z: number): UnitPoint =>
    axis === 'x' ? [across, along, z] : [along, across, z];

  const inward: UnitPoint =
    axis === 'x' ? [-sign(back), 0, 0] : [0, -sign(back), 0];
  const outward: UnitPoint =
    axis === 'x' ? [sign(back), 0, 0] : [0, sign(back), 0];
  const endward: UnitPoint =
    axis === 'x' ? [0, -sign(other), 0] : [-sign(other), 0, 0];

  /* The footprint, in order: inner-free, inner-corner, outer-corner, outer-free. The middle two are
     the mitre. */
  const footprint: [across: number, along: number][] = [
    [back, free],
    [back, other],
    [outer, cornerOuter],
    [outer, free],
  ];

  const endFace: UnitPoint[] = [
    at(back, free, 0),
    at(outer, free, 0),
    at(outer, free, 1),
    at(back, free, 1),
  ];

  const face = (
    kind: AxisBoxFaceKind,
    normal: UnitPoint,
    points: UnitPoint[]
  ) => ({
    kind,
    normal,
    points,
    outline: edgesOf(points),
  });

  return [
    face('wall', inward, [
      at(back, free, 0),
      at(back, other, 0),
      at(back, other, 1),
      at(back, free, 1),
    ]),
    face(
      'wallRim',
      [0, 0, 1],
      footprint.map(([across, along]) => at(across, along, 1))
    ),
    /* No underside. The plinth's plan reaches the walls' outer faces, so it already closes the
       solid from below and a wall's own bottom face is interior to the union - it was only ever
       covered by the plinth's fill. Keeping it also put a line back that this change removed: the
       edge where the wall's footprint meets the plinth's front is claimed by that front side, which
       below the plane is drawn in the same pass as the fill and so strokes after it, leaving the
       short tick at the foot of each wall. The base crease itself is not lost - the inner `wall`
       face draws it, and its normal is horizontal, so it is visible at any pitch. */
    face('wallRim', outward, [
      at(outer, cornerOuter, 0),
      at(outer, free, 0),
      at(outer, free, 1),
      at(outer, cornerOuter, 1),
    ]),
    {
      kind: 'wallRim',
      normal: endward,
      points: endFace,
      outline: edgesClearOfTheFloor(endFace),
    },
  ];
};

/**
 * The floor as a slab, a plinth under the plane.
 *
 * Its footprint reaches out to the walls' outer faces, so they stand on it rather than overhanging
 * it and the three slabs read as one object. The alternative - stopping the plinth at the data's own
 * extent - leaves the walls' thickness jutting past it, and the notch that opens at the corner is
 * more obviously wrong than the line it was meant to fix.
 *
 * Which is why the top face carries an `outline`. Its two outer edges are where the wall slabs sit
 * on it: interior to the union, and so lines that should not be drawn at all. Left in, each showed
 * as a second border running alongside a wall's base.
 */
const floorSlab = (
  [backX, backY]: BackCorner,
  thickness: number,
  heightScale: number
): AxisBoxFace[] => {
  const xOuter = backX + thickness * sign(backX);
  const yOuter = backY + thickness * sign(backY);
  const xFront = -backX;
  const yFront = -backY;
  /* Divided by the height scale, because the projector applies it to z and to nothing else:
     `scaledZ = z * camera.heightScale`. The walls spend their thickness on x and y, which arrive
     unscaled, so this is what makes all three slabs one thickness on screen at any relief. */
  const base = axisBoxBase(thickness, heightScale);

  const plan: [x: number, y: number][] = [
    [xOuter, yOuter],
    [xFront, yOuter],
    [xFront, yFront],
    [xOuter, yFront],
  ];
  const top = plan.map(([x, y]): UnitPoint => [x, y, 0]);

  const side = (
    axis: 'x' | 'y',
    at: number,
    normal: UnitPoint,
    /** The front sides carry the box's `z = 0` line; the outer two sit under the walls. */
    isFront: boolean
  ): AxisBoxFace => {
    const [from, to] = axis === 'x' ? [yOuter, yFront] : [xOuter, xFront];
    const back = axis === 'x' ? backY : backX;
    const point = (along: number, z: number): UnitPoint =>
      axis === 'x' ? [at, along, z] : [along, at, z];
    const points = [
      point(from, base),
      point(to, base),
      point(to, 0),
      point(from, 0),
    ];

    return {
      kind: 'plinthRim',
      normal,
      points,
      /* The front sides keep their `z = 0` edge, trimmed to the footprint - the same segment the
         floor's outline carries. Delegating it to the floor alone loses it exactly where it matters
         most: the floor is culled below the plane, these are not, and the front of the box is the
         nearest thing to the eye. Listing it on both is free, because a segment goes to the latest
         visible pass that claims it, so above the plane it is still stroked once.

         Trimmed rather than whole, for the reason the floor's own outline is: the strip beyond
         `back` has the wall's end above it, coplanar with this face, and a line there cuts across
         what reads as one flat surface. */
      outline: isFront
        ? [
            ...edgesClearOfTheFloor(points),
            [point(back, 0), point(to, 0)] as UnitSegment,
          ]
        : edgesClearOfTheFloor(points),
    };
  };

  return [
    {
      /* The plane the grid is ruled on and the data sits on. */
      kind: 'floor',
      normal: [0, 0, 1],
      points: top,
      /* The data footprint's two front edges, and nothing else. The plan's other two run under the
         walls, where the wall slab sits on the plinth - interior to the solid. And the front two are
         trimmed back from the plan to the footprint, because the strip beyond it is under a wall
         too: past that point the face above is the wall's end, coplanar with the plinth's front, and
         a line there cuts across one flat surface. */
      outline: [
        [
          [backX, yFront, 0],
          [xFront, yFront, 0],
        ],
        [
          [xFront, backY, 0],
          [xFront, yFront, 0],
        ],
      ],
    },
    {
      kind: 'plinthBase',
      normal: [0, 0, -1],
      points: plan.map(([x, y]): UnitPoint => [x, y, base]),
      outline: edgesOf(plan.map(([x, y]): UnitPoint => [x, y, base])),
    },
    side('x', xFront, [-sign(backX), 0, 0], true),
    side('y', yFront, [0, -sign(backY), 0], true),
    side('x', xOuter, [sign(backX), 0, 0], false),
    side('y', yOuter, [0, sign(backY), 0], false),
  ];
};

/**
 * The whole box: a plinth under the plane, and two wall slabs standing on it.
 *
 * The plane the data is read against stays exactly the data's footprint. Every bit of thickness
 * goes outward or downward from it - beyond the walls it bounds, beneath the floor it is - so the
 * grid and the ticks keep meaning what they mean.
 *
 * The walls' thickness therefore overhangs the plinth at the back, which is what a frame does and is
 * hidden behind the walls from any angle the box is drawn at.
 */
export const axisBoxFaces = (
  corner: BackCorner,
  thickness: number,
  /** The camera's, so the plinth can be as thick on screen as the walls are. */
  heightScale: number
): AxisBoxFace[] => [
  ...floorSlab(corner, thickness, heightScale),
  ...wallSlab('x', corner, thickness),
  ...wallSlab('y', corner, thickness),
];

/**
 * The interior tick positions, in [-1, 1], leaving out both ends.
 *
 * The ends are where a wall meets the floor or turns a corner, which the faces already draw as
 * their own edges. Ruling them again puts two strokes on one line.
 */
const interiorTicks = (count: number, inset: AxisInset): number[] =>
  toTickFractions(count, inset)
    .map(fraction => fraction * 2 - 1)
    .filter(position => Math.abs(position) !== 1);

/**
 * Grid lines ruled across the two walls' inner faces.
 *
 * The floor is left out: `renderAxes3d` already rules it, and ruling it twice doubles the ink on
 * the one plane that is always visible.
 *
 * Each wall gets lines in both directions — heights read off the vertical axis, and the other
 * direction carries the eye from a point on the surface down to the floor tick it belongs to.
 * That second family is what makes a wall more than a backdrop.
 *
 * Two counts, because the two families answer to different axes: the heights have to land on the
 * vertical axis's own labels, and the across lines have to meet the floor grid where it reaches
 * the wall. They were one number only while every axis was divided the same way.
 */
export const axisBoxGrid = (
  [backX, backY]: BackCorner,
  xTickCount: number,
  yTickCount: number,
  zTickCount: number,
  /** The fraction of each horizontal axis its padding takes at each end. */
  { x: xInset, y: yInset }: { x: AxisInset; y: AxisInset }
): UnitSegment[] => {
  /* Each wall ruled at the count of the axis it spans, not at a count shared between them. The
     wall standing at `backX` runs along y, so its uprights are divisions of y; the one at `backY`
     runs along x. Read the other way round they land between the floor rules they continue. */
  const alongY = interiorTicks(yTickCount, yInset);
  const alongX = interiorTicks(xTickCount, xInset);
  /* z runs 0..1, so the same fractions map straight onto height. */
  const heights = interiorTicks(zTickCount, [0, 0]).map(
    value => (value + 1) / 2
  );

  return [
    ...heights.flatMap((z): UnitSegment[] => [
      [
        [backX, -1, z],
        [backX, 1, z],
      ],
      [
        [-1, backY, z],
        [1, backY, z],
      ],
    ]),
    ...alongY.map((along): UnitSegment => [
      [backX, along, 0],
      [backX, along, 1],
    ]),
    ...alongX.map((along): UnitSegment => [
      [along, backY, 0],
      [along, backY, 1],
    ]),
  ];
};

/**
 * Puts a point on the cube, which the hovered sample is not always on.
 *
 * `grid.zRange` is the range of the *interpolated* cell values, not of the samples it was built
 * from, and every kernel shrinks that range: `smooth` by construction, `binned` wherever a cell
 * averages more than one sample, `linear` because a peak rarely lands on a cell centre. So the
 * tallest sample normalizes above 1 and the lowest below 0.
 *
 * Only z. x and y are normalized against the sample extents the grid was built from, so they are
 * inside [-1, 1] by construction.
 */
const ontoCube = ([x, y, z]: UnitPoint): UnitPoint => [
  x,
  y,
  Math.min(1, Math.max(0, z)),
];

/**
 * The three axis-parallel lines through a point, each spanning the whole box.
 *
 * Spans rather than stubs from the point to the nearest panel: a line that crosses the box reaches
 * the wall carrying its own ticks whichever corner the camera is at, so this needs no corner at
 * all, and the reader gets the whole line to follow rather than having to pick the right end.
 *
 * The point is put on the cube first. Left where it was, a sample above the fitted range hangs the
 * two horizontal spans over the top of the walls, touching no tick and so readable against nothing,
 * while the vertical span - which is the box's own height - stops short of the point it is supposed
 * to pass through. Clamped, all three stay coherent and on the box, and the height reported is the
 * top of the axis rather than the sample. That is the honest reading: the axis does not go that
 * high. The residual between a sample and the fit is what the marker's own tether is for.
 */
export const crosshairSegments = (point: UnitPoint): UnitSegment[] => {
  const [x, y, z] = ontoCube(point);

  return [
    [
      [-1, y, z],
      [1, y, z],
    ],
    [
      [x, -1, z],
      [x, 1, z],
    ],
    [
      [x, y, 0],
      [x, y, 1],
    ],
  ];
};
